import { useCallback, useMemo } from 'react';

import { Pencil } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import AISparkles from '@/components/common/AISparkles';
import Button from '@/components/common/Button';
import { useEmail } from '@/components/providers/EmailProvider/EmailProvider';
import { Checkbox } from '@/components/shadcn/checkbox';
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldLabel,
  FieldTitle,
} from '@/components/shadcn/field';
import ActionSourceIcon from '@/features/analysis/components/AnalysisActions/ActionSourceIcon';
import useAutoGenerateConceptReply from '@/features/compose/hooks/useAutoGenerateConceptReply';
import useReplyToClientActionsStore from '@/features/compose/stores/useReplyToClientActionsStore';
import type { ConceptReplyRequest } from '@/features/compose/types';
import getConceptReplyPrompt from '@/features/compose/utils/getConceptReplyPrompt';
import useEmailClient from '@/features/email/hooks/useEmailClient';
import paths from '@/lib/paths';
import cn from '@/lib/utils/cn';
import type { ActionType } from '@/types/actions';

const REPLY_ACTION_TYPES: ActionType[] = ['REPLY_TO_CLIENT'];

export default function ComposeSection() {
  const { t } = useTranslation();
  const actionsByMessageId = useReplyToClientActionsStore(
    (state) => state.actionsByMessageId,
  );
  const replyToClientActions = useReplyToClientActionsStore(
    (state) => state.actions,
  );

  const {
    email,
    analysis: { actions },
  } = useEmail();

  const checkedActions = useMemo(
    () => actionsByMessageId?.[email?.messageId ?? ''] ?? [],
    [actionsByMessageId, email?.messageId],
  );

  const setCheckedActions = (actions: Array<string>) =>
    replyToClientActions.updateActions(email?.messageId ?? '', actions);

  const { getEmailBody } = useEmailClient();

  const replyActions = (actions ?? []).filter((action) =>
    REPLY_ACTION_TYPES.includes(action.type),
  );

  const navigate = useNavigate();

  const handleGenerate = useCallback(
    async (source: ConceptReplyRequest['source'] = 'compose-button') => {
      const body = await getEmailBody('Html');

      const request: ConceptReplyRequest = {
        actions: checkedActions,
        currentConceptReply: body,
        prompt: getConceptReplyPrompt(body),
        source,
      };

      navigate(paths.conceptReply, { state: request });
    },
    [checkedActions, getEmailBody, navigate],
  );

  useAutoGenerateConceptReply(handleGenerate);

  const handleCheckedChange = (actionId: string) => (checked: boolean) =>
    setCheckedActions([
      ...checkedActions.filter((action) => action !== actionId),
      ...(checked ? [actionId] : []),
    ]);

  return (
    <div className="flex flex-col gap-2 border-b p-2">
      <div className="flex w-full items-center justify-between">
        <div className="flex h-7 items-center gap-1.5 text-sm">
          <Pencil className="size-3.5" />

          {t('COMPOSE_SECTION_TITLE')}
        </div>
      </div>

      <div className="flex flex-col gap-4">
        <p className="text-muted-foreground text-sm">
          {t('COMPOSE_SECTION_INTRO')}
        </p>

        <div className="flex flex-col items-end gap-2">
          {replyActions.map((action) => (
            <FieldLabel
              key={action.id}
              className={cn(
                'group relative cursor-pointer border-none bg-neutral-50 dark:bg-neutral-900',
                'has-data-[state=checked]:bg-primary-50 has-data-[state=checked]:border-primary-50',
                'dark:has-data-[state=checked]:bg-primary-800/50 dark:has-data-[state=checked]:border-primary-800/50',
              )}
            >
              <Field
                key={action.id}
                orientation="horizontal"
                className="gap-2 p-2!"
              >
                <Checkbox
                  id={action.id}
                  name={action.id}
                  className="bg-white"
                  checked={checkedActions.includes(action.id)}
                  onCheckedChange={handleCheckedChange(action.id)}
                />

                <FieldContent className="gap-0">
                  <FieldTitle className="">
                    {action.title}

                    <ActionSourceIcon
                      source={action.source}
                      className="group-has-data-[state=checked]:bg-primary-200/30 dark:group-has-data-[state=checked]:bg-primary-500/30 bg-neutral-200/70 dark:bg-neutral-800"
                    />
                  </FieldTitle>
                  <FieldDescription className="text-[13px]">
                    {action.description}
                  </FieldDescription>
                </FieldContent>
              </Field>
            </FieldLabel>
          ))}

          <Button onSubmit={() => handleGenerate()}>
            <AISparkles className="size-4 text-inherit" />

            {t('COMPOSE_SECTION_GENERATE_BUTTON')}
          </Button>
        </div>
      </div>
    </div>
  );
}
